import React from 'react';
import { ShieldCheck, CreditCard, Star, Users, BookOpen, Settings, ArrowRight, CheckCircle2, XCircle, Zap, Crosshair, ArrowDownToLine, Eye, Frown, Gift } from 'lucide-react';
import { painPoints, modules, bonusCards, testimonials, objections } from './src/data/content';
import { Accordion } from './src/components/Accordion';
import { Countdown } from './src/components/Countdown';
import { SocialProofPopup } from './src/components/SocialProofPopup';
import { MetaPixel } from './src/components/MetaPixel';
function scrollToCompra(e: React.MouseEvent) {
  e.preventDefault();
  const el = document.getElementById('compra');
  if (el) {
    el.scrollIntoView({
      behavior: 'smooth'
    });
  }
}
const painIcons = [Zap, Crosshair, ArrowDownToLine, Eye, Users, Frown];
export function App() {
  return <div className="min-h-screen bg-[#050505] text-gray-200 font-sans selection:bg-lime-400 selection:text-black" data-id="element-0">
      <MetaPixel data-id="element-1" />
      <SocialProofPopup data-id="element-2" />

      {/* HERO SECTION */}
      <header className="pt-16 pb-12 px-4 max-w-4xl mx-auto text-center" data-id="element-3">
        <h1 className="text-4xl md:text-6xl font-display uppercase tracking-tight text-white mb-8 leading-tight" data-id="element-4">
          Convertí Tus Años de Tenis en una{' '}
          <span className="text-lime-400" data-id="element-5">Ventaja</span>, No en un Problema
        </h1>

        <div className="relative mx-auto mb-8 cursor-pointer group" onClick={scrollToCompra} data-id="element-6">
          <div className="absolute inset-0 bg-lime-400/20 blur-3xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-7"></div>
          <img src="https://cdn.magicpatterns.com/uploads/dcWyT7kMBA1zvP3cm8aT4D/ChatGPT_Image_Jun_13,_2026,_05_00_09_PM.png" alt="Tenis a Padel Pro Book" className="w-full max-w-2xl mx-auto rounded-xl shadow-2xl relative z-10 border border-dark-700" data-id="element-8" />
        </div>

        <h2 className="text-base md:text-lg font-medium text-gray-300 mb-5 max-w-2xl mx-auto leading-snug" data-id="element-9">
          Una guía paso a paso para eliminar los vicios del tenis, entender las
          paredes, dominar la táctica del pádel y completar una transición real
          en solo 8 semanas.
        </h2>

        <button onClick={scrollToCompra} className="w-full md:w-auto px-8 py-5 bg-lime-400 hover:bg-lime-500 text-black font-display text-2xl uppercase tracking-wide rounded-lg shadow-[0_0_30px_rgba(200,255,0,0.3)] hover:shadow-[0_0_50px_rgba(200,255,0,0.5)] transition-all transform hover:scale-105 flex items-center justify-center mx-auto space-x-3" data-id="element-10">
          <span data-id="element-11">Quiero usar el tenis como ventaja</span>
          <ArrowRight className="w-6 h-6" data-id="element-12" />
        </button>

        <div className="flex flex-wrap justify-center items-center gap-4 mt-8 text-sm text-gray-400 font-medium" data-id="element-13">
          <span className="flex items-center" data-id="element-14">
            <ShieldCheck className="w-4 h-4 mr-1 text-lime-400" data-id="element-15" /> Garantía 7
            días
          </span>
          <span className="hidden md:inline" data-id="element-16">•</span>
          <span className="flex items-center" data-id="element-17">
            <ShieldCheck className="w-4 h-4 mr-1 text-lime-400" data-id="element-18" /> Pago 100%
            seguro
          </span>
          <span className="hidden md:inline" data-id="element-19">•</span>
          <span className="flex items-center" data-id="element-20">
            <CreditCard className="w-4 h-4 mr-1 text-lime-400" data-id="element-21" /> Mercado Pago
          </span>
          <span className="hidden md:inline" data-id="element-22">•</span>
          <span className="flex items-center" data-id="element-23">
            <BookOpen className="w-4 h-4 mr-1 text-lime-400" data-id="element-24" /> PDF digital
          </span>
        </div>
      </header>

      {/* PAIN POINTS SECTION */}
      <section className="py-16 bg-dark-900 border-y border-dark-800" data-id="element-25">
        <div className="max-w-3xl mx-auto px-4" data-id="element-26">
          <h2 className="text-3xl md:text-4xl font-display text-center mb-10 text-white" data-id="element-27">
            ¿Sentís que no estás aprovechando tu potencial?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-28">
            {painPoints.map((point, idx) => {
            const Icon = painIcons[idx % painIcons.length];
            return <div key={idx} className="group flex items-start gap-4 bg-dark-800 p-5 rounded-xl border border-dark-700 hover:border-red-500/50 transition-colors" data-id="element-29">
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center justify-center" data-id="element-30">
                    <Icon className="w-6 h-6 text-red-500" data-id="element-31" />
                  </div>
                  <p className="text-base text-gray-300 leading-snug" data-id="element-32">
                    {point}
                  </p>
                </div>;
          })}
          </div>
        </div>
      </section>

      {/* MODULES SECTION */}
      <section className="py-20 px-4 max-w-3xl mx-auto" data-id="element-33">
        <h2 className="text-3xl md:text-4xl font-display text-center mb-10 text-lime-400" data-id="element-34">
          Para eso hicimos esto:
        </h2>
        <div className="space-y-4" data-id="element-35">
          {modules.map((mod, idx) => <Accordion key={idx} title={mod.title} data-id="element-36">
              <div className="space-y-6" data-id="element-37">
                {mod.content.map((sub, sIdx) => <div key={sIdx} data-id="element-38">
                    <h4 className="font-bold text-white mb-2" data-id="element-39">
                      {sub.subtitle}
                    </h4>
                    <ul className="space-y-2" data-id="element-40">
                      {sub.items.map((item, iIdx) => <li key={iIdx} className="flex items-start" data-id="element-41">
                          <CheckCircle2 className="w-5 h-5 text-lime-400 mr-2 flex-shrink-0 mt-0.5" data-id="element-42" />
                          <span className="text-gray-300" data-id="element-43">{item}</span>
                        </li>)}
                    </ul>
                  </div>)}
              </div>
            </Accordion>)}
        </div>

        <div className="mt-12 text-center" data-id="element-44">
          <button onClick={scrollToCompra} className="w-full md:w-auto px-8 py-4 bg-dark-800 border border-lime-400 hover:bg-lime-400 hover:text-black text-lime-400 font-display text-xl uppercase tracking-wide rounded-lg transition-all" data-id="element-45">
            Ver Oferta Completa
          </button>
        </div>
      </section>

      {/* BONUSES SECTION */}
      <section className="py-20 bg-dark-900 border-y border-dark-800" data-id="element-46">
        <div className="max-w-4xl mx-auto px-4" data-id="element-47">
          <div className="text-center mb-12" data-id="element-48">
            <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-lime-400 bg-lime-400/10 border border-lime-400/30 rounded-full px-4 py-1.5 mb-4" data-id="element-49">
              Gratis por hoy
            </span>
            <h2 className="text-3xl md:text-4xl font-display text-white mb-3" data-id="element-50">
              🎁 Kit de Transición Completo
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto" data-id="element-51">
              Todo lo que necesitás para dejar de jugar como tenista — desde el
              primer día.
            </p>
          </div>

          <div className="space-y-6" data-id="element-52">
            {bonusCards.map((bonus, idx) => <div key={idx} className="bg-dark-800 border border-dark-700 rounded-2xl overflow-hidden md:flex hover:border-lime-400/40 transition-colors" data-id="element-53">
                <div className="relative md:w-2/5 h-44 md:h-auto overflow-hidden" data-id="element-54">
                  <img src={bonus.image} alt={`Vista previa de ${bonus.title}`} className="w-full h-full object-cover object-top" data-id="element-55" />
                  <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-dark-800 via-dark-800/30 to-transparent" data-id="element-56" />
                </div>
                <div className="p-6 md:w-3/5 flex flex-col" data-id="element-57">
                  <div className="flex items-center gap-2 mb-2" data-id="element-58">
                    <span className="text-2xl" data-id="element-59">{bonus.emoji}</span>
                    <span className="text-xs font-bold uppercase tracking-widest text-lime-400" data-id="element-60">
                      {bonus.label}
                    </span>
                  </div>
                  <h3 className="text-xl md:text-2xl font-display text-white leading-tight mb-1" data-id="element-61">
                    {bonus.title}
                  </h3>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-3" data-id="element-62">
                    {bonus.meta}
                  </p>
                  <p className="text-sm text-gray-300 leading-relaxed mb-4" data-id="element-63">
                    {bonus.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-auto" data-id="element-64">
                    {bonus.chips.map((chip, cIdx) => <span key={cIdx} className="text-xs text-gray-200 bg-dark-900 border border-dark-700 rounded-full px-3 py-1" data-id="element-65">
                        {chip}
                      </span>)}
                  </div>
                </div>
              </div>)}
          </div>

          <div className="mt-12 text-center" data-id="element-66">
            <button onClick={scrollToCompra} className="w-full md:w-auto px-8 py-4 bg-lime-400 hover:bg-lime-500 text-black font-display text-xl uppercase tracking-wide rounded-lg shadow-[0_0_30px_rgba(200,255,0,0.3)] transition-all transform hover:scale-105" data-id="element-67">
              Quiero usar el tenis como ventaja
            </button>
          </div>
        </div>
      </section>

      {/* COMPARISON SECTION */}
      <section className="py-14 px-4 max-w-4xl mx-auto" data-id="element-68">
        <h2 className="text-2xl md:text-4xl font-display text-center mb-8 text-white" data-id="element-69">
          La Diferencia es Clara
        </h2>
        <div className="grid grid-cols-2 gap-3 md:gap-5" data-id="element-70">
          <div className="bg-dark-800 border border-red-500/30 rounded-xl p-3 md:p-5" data-id="element-71">
            <h3 className="text-sm md:text-2xl font-display text-red-500 mb-4 text-center leading-tight" data-id="element-72">
              Sin Tenis a Pádel Pro
            </h3>
            <ul className="space-y-2 md:space-y-3" data-id="element-73">
              {['Perdés contra jugadores con peor técnica', 'Tus voleas se van largas o a la red', 'Las paredes te generan inseguridad', 'Te frustrás y sentís que no avanzás', 'Jugás al pádel como si fuera tenis'].map((item, i) => <li key={i} className="flex items-start" data-id="element-74">
                  <XCircle className="w-3.5 h-3.5 md:w-5 md:h-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-75" />
                  <span className="text-xs md:text-base text-gray-300 leading-snug" data-id="element-76">
                    {item}
                  </span>
                </li>)}
            </ul>
          </div>
          <div className="bg-dark-800 border border-lime-400 rounded-xl p-3 md:p-5 relative overflow-hidden" data-id="element-77">
            <div className="absolute top-0 right-0 w-32 h-32 bg-lime-400/10 rounded-full blur-3xl" data-id="element-78"></div>
            <h3 className="text-sm md:text-2xl font-display text-lime-400 mb-4 text-center relative z-10 leading-tight" data-id="element-79">
              Con Tenis a Pádel Pro
            </h3>
            <ul className="space-y-2 md:space-y-3 relative z-10" data-id="element-80">
              {['Usás tu técnica de tenis como ventaja real', 'Dominás el bloqueo y la construcción', 'Leés las paredes con anticipación', 'Subís de nivel rápido y sin frustración', 'Entendés la táctica y jugás en equipo'].map((item, i) => <li key={i} className="flex items-start" data-id="element-81">
                  <CheckCircle2 className="w-3.5 h-3.5 md:w-5 md:h-5 text-lime-400 mr-2 flex-shrink-0 mt-0.5" data-id="element-82" />
                  <span className="text-xs md:text-base text-gray-100 font-medium leading-snug" data-id="element-83">
                    {item}
                  </span>
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* PURCHASE SECTION */}
      <section id="compra" className="py-20 bg-dark-900 border-y border-dark-800 relative overflow-hidden" data-id="element-84">
        <div className="absolute inset-0 bg-lime-400/5 blur-[100px] pointer-events-none" data-id="element-85"></div>

        <div className="max-w-2xl mx-auto px-4 text-center relative z-10" data-id="element-86">
          <h2 className="text-3xl md:text-5xl font-display text-white mb-3" data-id="element-87">
            Empezá tu transición hoy
          </h2>
          <p className="text-base md:text-lg text-lime-400 font-bold mb-8 uppercase tracking-wide" data-id="element-88">
            Los 5 bonos son GRATIS por el día de hoy
          </p>

          {/* UNIFIED OFFER CARD */}
          <div className="bg-dark-800 border-2 border-lime-400 rounded-2xl overflow-hidden shadow-[0_0_50px_rgba(200,255,0,0.15)] text-left" data-id="element-89">
            {/* Countdown banner */}
            <div className="bg-dark-900/60 border-b border-dark-700 px-6 py-4 text-center" data-id="element-90">
              <p className="text-xs uppercase tracking-widest text-gray-400 mb-2" data-id="element-91">
                La oferta termina en
              </p>
              <Countdown data-id="element-92" />
            </div>

            {/* VALUE STACK */}
            <div className="p-5 md:p-7" data-id="element-93">
              <h3 className="text-lg md:text-xl font-display text-white text-center mb-5" data-id="element-94">
                Esto es todo lo que te llevás hoy
              </h3>
              <h2 className="text-3xl md:text-5xl font-display text-white mb-3" data-id="element-95">
                Empezá tu transición hoy
              </h2>
              <p className="text-base md:text-lg text-lime-400 font-bold mb-8 uppercase tracking-wide" data-id="element-96">
                Los 5 bonos son GRATIS por el día de hoy
              </p>
            </div>
            {/* UNIFIED OFFER CARD */}
            <div className="bg-dark-800 border-2 border-lime-400 rounded-2xl overflow-hidden shadow-[0_0_50px_rgba(200,255,0,0.15)] text-left" data-id="element-97">
              {/* Countdown banner */}
              <div className="bg-dark-900/60 border-b border-dark-700 px-6 py-4 text-center" data-id="element-98">
                <p className="text-xs uppercase tracking-widest text-gray-400 mb-2" data-id="element-99">
                  La oferta termina en
                </p>
                <Countdown data-id="element-100" />
              </div>

              {/* VALUE STACK */}
              <div className="p-5 md:p-7" data-id="element-101">
                <h3 className="text-lg md:text-xl font-display text-white text-center mb-5" data-id="element-102">
                  Esto es todo lo que te llevás hoy
                </h3>
                <ul className="space-y-2.5 mb-5" data-id="element-103">
                  {[{
                  label: 'Guía completa "Tenis a Pádel Pro" (6 módulos)',
                  value: '$39.900'
                }, {
                  label: 'BONUS 1 — Planilla de las 8 semanas',
                  value: '$4.900',
                  bonus: true
                }, {
                  label: 'BONUS 2 — Checklist pre-partido del tenista',
                  value: '$2.900',
                  bonus: true
                }, {
                  label: 'BONUS 3 — Glosario tenis → pádel',
                  value: '$2.900',
                  bonus: true
                }, {
                  label: 'BONUS 4 — Los 7 golpes que el tenista ejecuta mal',
                  value: '$3.900',
                  bonus: true
                }, {
                  label: 'BONUS 5 — Mini guía táctica de los primeros 10 puntos',
                  value: '$3.900',
                  bonus: true
                }].map((row, i) => <li key={i} className="flex items-center justify-between gap-3 border-b border-dark-700/60 pb-2.5 last:border-0 last:pb-0" data-id="element-104">
                      <span className="flex items-start gap-2 text-sm md:text-base text-gray-200" data-id="element-105">
                        {row.bonus ? <Gift className="w-4 h-4 md:w-5 md:h-5 text-lime-400 flex-shrink-0 mt-0.5" data-id="element-106" /> : <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-lime-400 flex-shrink-0 mt-0.5" data-id="element-107" />}
                        <span className={row.bonus ? 'text-gray-300' : 'font-medium'} data-id="element-108">
                          {row.label}
                        </span>
                      </span>
                      {row.bonus ? <span className="text-sm font-bold text-lime-400 whitespace-nowrap uppercase" data-id="element-109">
                          Gratis
                        </span> : <span className="text-sm text-gray-400 whitespace-nowrap" data-id="element-110">
                          {row.value}
                        </span>}
                    </li>)}
                </ul>
                <div className="flex items-center justify-between border-t border-dark-700 pt-4" data-id="element-111">
                  <span className="text-sm md:text-base text-gray-300 uppercase tracking-wide font-semibold" data-id="element-112">
                    Valor total
                  </span>
                  <span className="text-xl md:text-2xl font-display text-gray-500 line-through" data-id="element-113">
                    $55.900
                  </span>
                </div>
              </div>

              {/* Price + CTA */}
              <div className="bg-dark-900/60 border-t border-dark-700 px-5 md:px-7 py-7 text-center" data-id="element-114">
                <div className="mb-5" data-id="element-115">
                  <span className="text-gray-500 line-through text-xl md:text-2xl font-display mr-3" data-id="element-116">
                    $55.900
                  </span>
                  <span className="text-5xl md:text-6xl font-display text-white" data-id="element-117">
                    $19.990
                  </span>
                  <span className="text-lime-400 font-bold ml-2" data-id="element-118">ARS</span>
                </div>

                <a href="https://checkout.ebookpoint.shop/cart/59647727304785:1" className="block w-full px-8 py-5 bg-lime-400 hover:bg-lime-500 text-black font-display text-xl md:text-2xl uppercase tracking-wide rounded-xl shadow-[0_0_30px_rgba(200,255,0,0.3)] hover:shadow-[0_0_50px_rgba(200,255,0,0.5)] transition-all transform hover:scale-105 mb-5" data-id="element-119">
                  Quiero usar el tenis como ventaja
                </a>

                <div className="flex flex-col sm:flex-row sm:justify-center gap-3 sm:gap-6 text-sm text-gray-400" data-id="element-120">
                  <div className="flex justify-center items-center space-x-2" data-id="element-121">
                    <ShieldCheck className="w-5 h-5 text-lime-400" data-id="element-122" />
                    <span data-id="element-123">Garantía de 7 días</span>
                  </div>
                  <div className="flex justify-center items-center space-x-2" data-id="element-124">
                    <CreditCard className="w-5 h-5 text-lime-400" data-id="element-125" />
                    <span data-id="element-126">Pago seguro · Mercado Pago</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS SECTION */}
      <section className="py-20 px-4 max-w-5xl mx-auto" data-id="element-127">
        <h2 className="text-3xl md:text-4xl font-display text-center mb-12 text-white" data-id="element-128">
          Lo que dicen otros tenistas
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-129">
          {testimonials.map((test, idx) => <div key={idx} className="bg-dark-800 p-6 rounded-xl border border-dark-700 flex flex-col" data-id="element-130">
              <div className="flex text-lime-400 mb-4" data-id="element-131">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" data-id="element-132" />)}
              </div>
              <p className="text-gray-300 italic mb-6 flex-grow" data-id="element-133">
                "{test.text}"
              </p>
              <div className="mt-auto" data-id="element-134">
                <p className="font-bold text-white" data-id="element-135">{test.name}</p>
                <p className="text-sm text-gray-500" data-id="element-136">{test.city}</p>
              </div>
            </div>)}
        </div>
      </section>

      {/* OBJECTIONS SECTION */}
      <section className="py-20 bg-dark-900 border-t border-dark-800" data-id="element-137">
        <div className="max-w-3xl mx-auto px-4" data-id="element-138">
          <h2 className="text-3xl md:text-4xl font-display text-center mb-10 text-white" data-id="element-139">
            Preguntas Frecuentes
          </h2>
          <div className="space-y-4" data-id="element-140">
            {objections.map((obj, idx) => <Accordion key={idx} title={obj.q} data-id="element-141">
                <p className="text-gray-300 leading-relaxed" data-id="element-142">{obj.a}</p>
              </Accordion>)}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-12 text-center text-gray-500 border-t border-dark-800" data-id="element-143">
        <div className="flex flex-col items-center justify-center space-y-4" data-id="element-144">
          <Settings className="w-8 h-8 text-dark-700" data-id="element-145" />
          <p data-id="element-146">
            Soporte:{' '}
            <a href="mailto:EbookDreamShop@gmail.com" className="text-lime-400 hover:underline" data-id="element-147">
              EbookDreamShop@gmail.com
            </a>
          </p>
          <p className="text-sm mt-8" data-id="element-148">
            © {new Date().getFullYear()} Tenis a Pádel Pro. Todos los derechos
            reservados.
          </p>
        </div>
      </footer>
    </div>;
}